/**
 * Grundtypen aller Nahtstellen ([V] 4.3 `common.ts`, Plan Welle 0 Teil A Anhang A.common).
 *
 * - Marken-Typen (`UserId`, `PersonId`, `RawText`) sind aus `string` nicht zuweisbar; sie entstehen nur
 *   durch die Schemas (`UserIdSchema`) bzw. durch die zuständige Schicht.
 * - `DbTx` ist opak: nur die Datenbankschicht (und `testing/harness.ts`) erzeugt Werte.
 * - Fehler: `DomainResult` für Methoden mit Rückgabewert, `DomainFailure` für `void`-Methoden (R-25):
 *   fremde oder unsichtbare IDs → `NOT_FOUND` ([V] 4.3 Regel für alle Ports).
 * - `Problem` ist das RFC-9457-Fehlerformat der HTTP-Schnittstelle ([V] E-p1); `type` lautet
 *   `urn:socialfy:problem:<code>` (R-21).
 *
 * Diese Datei ist browser-nah (über `api-contract` im Dashboard): kein `node:`-Import.
 */
import { z } from 'zod';

/** Kennung eines Benutzers (UUID); nur über `UserIdSchema` oder die Sitzungsschicht erzeugbar. */
export type UserId = string & { readonly __brand: 'UserId' };
/** Kennung einer Person (UUID); Personen sind mandantenweit, Sichtbarkeit über `person_link` [A §6.1.3]. */
export type PersonId = string & { readonly __brand: 'PersonId' };
/** Opake Datenbanktransaktion; wird nur von Fakes und der Datenbankschicht erzeugt. */
export interface DbTx {
  readonly __dbTx: unique symbol;
}
/** Kontext von Workflows und Hintergrundläufen im Namen eines Benutzers. */
export type SystemContext = DomainContext & { system: true };
export type DataMode = 'live' | 'demo';
/** Kontext jedes Port-Aufrufs: Eigentümer, Datenmodus, Abbruch, Uhr (einzige Zeitquelle) und Anfrage-ID. */
export type DomainContext = {
  userId: UserId;
  dataMode: DataMode;
  signal: AbortSignal;
  now: () => Date;
  requestId: string;
};
export type DomainErrorCode =
  | 'NOT_FOUND'
  | 'INVALID_INPUT'
  | 'CONFLICT'
  | 'CAPABILITY_MISSING'
  | 'UNAVAILABLE'
  | 'BLOCKED_PERSON'
  | 'CONSENT_REQUIRED'
  | 'OPTED_OUT'
  | 'LIMIT_REACHED'
  | 'ACTION_DISABLED'
  | 'QUALITY_CHECK_FAILED';
export type DomainError = {
  code: DomainErrorCode;
  message: string;
  reason?: string;
  details?: Record<string, unknown>;
};
export type DomainResult<T> = { ok: true; value: T } | { ok: false; error: DomainError };

/**
 * Ausnahme für Port-Methoden ohne `DomainResult` (`Promise<void>`), z. B. `NOT_FOUND` bei fremden IDs (R-25).
 * Explizites Feld statt Parameter-Property (F6: Node-Typ-Stripping).
 */
export class DomainFailure extends Error {
  override readonly name = 'DomainFailure';
  readonly error: DomainError;
  constructor(error: DomainError) {
    super(error.code);
    this.error = error;
  }
}

/** Herkunft eines Auslösers ([V] E-j Nr. 3); `third_party_content` = Fremdinhalt (Prompt-Injection-Schutz). */
export const TriggerOrigin = z.enum(['mcp', 'dashboard', 'system', 'third_party_content']);
export type TriggerOrigin = z.infer<typeof TriggerOrigin>;

/** Gemeinsame Fehlercodes der HTTP-Schnittstelle ([V] E-p1); modulspezifische Werte stehen in `reason`. */
export const ProblemCode = z.enum([
  'unauthorized',
  'forbidden',
  'not_found',
  'validation',
  'conflict',
  'demo_read_only',
  'rate_limited',
  'gone',
  'unprocessable',
  'unavailable',
  'action_disabled',
  'internal',
  'payload_too_large', // v12 (D-C1): HTTP 413, Rumpf über der Grenze
]);
/** RFC 9457 Problem Details ([A] 5.12, [V] E-p1). Kein `.meta({ id })` hier (F10: nur in `api-contract`). */
export const Problem = z.object({
  type: z.string().url(),
  title: z.string(),
  status: z.int(),
  code: ProblemCode,
  reason: z.string().optional(),
  detail: z.string().optional(),
  requestId: z.string(),
  fieldErrors: z.record(z.string(), z.array(z.string())).optional(),
});
export type Problem = z.infer<typeof Problem>;

/** Ungeprüfter Fremdtext (Nachrichten, Namen): darf nur über `untrusted` in Ausgaben gelangen [MCP §10]. */
export type RawText = string & { readonly __brand: 'RawText' };
export type Page<T> = { items: T[]; nextCursor: string | null };
export const UserIdSchema = z.uuid().transform((s) => s as UserId);
export const PersonIdSchema = z.uuid().transform((s) => s as PersonId);

/** Einheitlicher Einhängepunkt für Barrieren-Tests ([A §4.1] TestBarrier; RV-14); in Produktion No-op. */
export interface TestHooks {
  at(point: string): Promise<void>;
}

/** Problem.type = `urn:socialfy:problem:<code>` (R-21). */
export const problemType = (code: z.infer<typeof ProblemCode>): string =>
  `urn:socialfy:problem:${code}`;
